import { type Dispatch, type SetStateAction, useMemo, useState } from 'react';
import type { LayoutItem } from 'react-grid-layout';
import { usePages, useUpdatePageTemplate } from '../../../../api/pages-client/pages-client.hooks';
import type {
  Component,
  DataEntryComponent,
  PageTemplate,
  PageTemplateConfig,
} from '../../../../api/pages-client/pages-client.type';
import { createDefaultLayout, layoutToLayoutData } from '../../dashboards.helpers';
import { convertComponentsToTemplateConfig } from './dashboard-template-helpers';
import type { PendingSave, SaveTemplateBanner } from './dashboard-template.type';

/**
 * Counts how many dashboards were created from a given template, used to warn
 * the user before saving template edits that cascade to existing dashboards.
 *
 * @param {string | undefined} templateId - The template whose children to count
 * @param {string} organizationId - Active organization id (passed to the pages query)
 * @param {string} portfolioId - Active portfolio id (passed to the pages query)
 * @returns {{ childPageCount: number }} The number of dashboards referencing the template
 */
const useChildPageCount = (
  templateId: string | undefined,
  organizationId: string,
  portfolioId: string,
): { childPageCount: number } => {
  const { pages } = usePages(organizationId, portfolioId, 'dashboard', 'dashboard');
  const childPageCount = useMemo(
    () => (templateId ? pages.filter((page) => page.page_template === templateId).length : 0),
    [pages, templateId],
  );
  return { childPageCount };
};

type UseTemplateSaveActionsArgs = {
  pageTemplate: PageTemplate | undefined;
  templateId: string | undefined;
  components: Component[];
  setComponents: Dispatch<SetStateAction<Component[]>>;
  dataEntryComponents: DataEntryComponent[];
  setDataEntryComponents: Dispatch<SetStateAction<DataEntryComponent[]>>;
  currentLayout: LayoutItem[];
  setCurrentLayout: Dispatch<SetStateAction<LayoutItem[]>>;
  originalLayout: LayoutItem[];
  setOriginalLayout: Dispatch<SetStateAction<LayoutItem[]>>;
  objectKeyMappings: Record<string, string>;
};

/**
 * Owns the template-edit save flow: layout edit mode, the edit-details and
 * add-widget modals, and the "Save & update children?" confirmation modal.
 *
 * Wraps the `useUpdatePageTemplate` mutation and exposes handlers for every
 * way a save can be initiated (save layout, add widget, edit widget, delete
 * widget, edit details) plus the confirm/cancel handlers for the cascade
 * confirmation modal. The container retains ownership of `components`,
 * `currentLayout`, `dataEntryComponents`, and `originalLayout` because the
 * apply-mappings flow (not part of the save flow) also writes them.
 *
 * @param {UseTemplateSaveActionsArgs} args - Container-owned state and setters the save flow reads/writes
 * @returns Save-flow state, modal-open setters, and handlers for the JSX layer
 */
const useTemplateSaveActions = ({
  pageTemplate,
  templateId,
  components,
  setComponents,
  dataEntryComponents,
  setDataEntryComponents,
  currentLayout,
  setCurrentLayout,
  originalLayout,
  setOriginalLayout,
  objectKeyMappings,
}: UseTemplateSaveActionsArgs) => {
  const updateTemplateMutation = useUpdatePageTemplate();

  const [isEditingLayout, setIsEditingLayout] = useState(false);
  const [isEditDetailsOpen, setIsEditDetailsOpen] = useState(false);
  const [isAddWidgetOpen, setIsAddWidgetOpen] = useState(false);
  const [isSaveTemplateModalOpen, setIsSaveTemplateModalOpen] = useState(false);
  const [pendingSave, setPendingSave] = useState<PendingSave | null>(null);
  const [banner, setBanner] = useState<SaveTemplateBanner | null>(null);

  const templateHasWidgets = components.length > 0 || dataEntryComponents.length > 0;

  const buildNextConfig = (nextComponents: Component[], nextDataEntry: DataEntryComponent[]): PageTemplateConfig => {
    return convertComponentsToTemplateConfig(
      nextComponents,
      nextDataEntry,
      objectKeyMappings,
      pageTemplate?.config?.inputs ?? [],
    );
  };

  const openConfirmSave = (next: PendingSave) => {
    setPendingSave(next);
    setIsSaveTemplateModalOpen(true);
  };

  // Folds a `LayoutItem[]` into each component's `layout_data` so widget edits
  // (rename/add/delete) preserve the live resize state instead of writing the
  // modal's stale layout back to the template.
  const syncWithLayout = (
    layout: LayoutItem[],
    baseComponents: Component[],
    baseDataEntry: DataEntryComponent[],
  ): { nextComponents: Component[]; nextDataEntry: DataEntryComponent[] } => {
    const nextComponents = baseComponents.map((component) => {
      const layoutItem = layout.find((item) => item.i === component.id);
      return layoutItem ? { ...component, layout_data: layoutToLayoutData(layoutItem) } : component;
    });
    const nextDataEntry = baseDataEntry.map((component) => {
      const layoutItem = layout.find((item) => item.i === component.id);
      return layoutItem ? { ...component, layout_data: layoutToLayoutData(layoutItem) } : component;
    });
    return { nextComponents, nextDataEntry };
  };

  const handleStartEditLayout = () => {
    setOriginalLayout([...currentLayout]);
    setIsEditingLayout(true);
  };

  const handleSaveLayout = () => {
    if (!pageTemplate || !templateId) return;
    const { nextComponents, nextDataEntry } = syncWithLayout(currentLayout, components, dataEntryComponents);
    const nextConfig = buildNextConfig(nextComponents, nextDataEntry);
    openConfirmSave({
      nextConfig,
      nextComponents,
      nextDataEntryComponents: nextDataEntry,
      nextLayout: currentLayout,
    });
  };

  const handleCancelLayout = () => {
    const hasChangedLayout = JSON.stringify(currentLayout) !== JSON.stringify(originalLayout);
    if (hasChangedLayout) {
      if (!confirm('Discard layout changes?')) {
        return;
      }
      setCurrentLayout(originalLayout);
    }
    setIsEditingLayout(false);
  };

  const handleResetLayout = () => {
    const resetLayouts = components.map((comp, index) => createDefaultLayout(comp.id || `component-${index}`, index));
    setCurrentLayout(resetLayouts);
  };

  const handleEditDetails = (data: { name: string; description: string }) => {
    if (!pageTemplate || !templateId) return;
    updateTemplateMutation.mutate(
      { id: templateId, payload: { name: data.name, description: data.description } },
      {
        onSuccess: () => {
          setBanner({ color: 'green', text: 'Template details updated.' });
          setIsEditDetailsOpen(false);
        },
        onError: () => {
          setBanner({ color: 'red', text: 'Failed to update template details.' });
          setIsEditDetailsOpen(false);
        },
      },
    );
  };

  const handleAddWidget = (widgetData: Component) => {
    const newId = `template-component-new-${Date.now()}`;
    const newComponent: Component = {
      ...widgetData,
      id: newId,
      page: 'template-preview',
    };
    const mergedComponents = [...components, newComponent];
    const nextLayout = [...currentLayout, createDefaultLayout(newId, currentLayout.length)];
    const { nextComponents, nextDataEntry } = syncWithLayout(nextLayout, mergedComponents, dataEntryComponents);
    const nextConfig = buildNextConfig(nextComponents, nextDataEntry);
    setIsAddWidgetOpen(false);
    openConfirmSave({
      nextConfig,
      nextComponents,
      nextDataEntryComponents: nextDataEntry,
      nextLayout,
    });
  };

  const handleEditWidget = (updatedWidget: Component) => {
    if (!updatedWidget.id) return;
    // Spread the existing component first so fields the modal doesn't echo back
    // (notably `layout_data`) aren't dropped — see `applyLayoutToComponents` for
    // the matching reason `syncWithLayout` is still needed for live drag state.
    const mergedComponents = components.map((component) =>
      component.id === updatedWidget.id ? { ...component, ...updatedWidget, page: 'template-preview' } : component,
    );
    const { nextComponents, nextDataEntry } = syncWithLayout(currentLayout, mergedComponents, dataEntryComponents);
    const nextConfig = buildNextConfig(nextComponents, nextDataEntry);
    openConfirmSave({
      nextConfig,
      nextComponents,
      nextDataEntryComponents: nextDataEntry,
      nextLayout: currentLayout,
    });
  };

  const handleDeleteWidget = (componentId: string) => {
    const filteredComponents = components.filter((component) => component.id !== componentId);
    const nextLayout = currentLayout.filter((item) => item.i !== componentId);
    const { nextComponents, nextDataEntry } = syncWithLayout(nextLayout, filteredComponents, dataEntryComponents);
    const nextConfig = buildNextConfig(nextComponents, nextDataEntry);
    openConfirmSave({
      nextConfig,
      nextComponents,
      nextDataEntryComponents: nextDataEntry,
      nextLayout,
    });
  };

  const handleConfirmSave = () => {
    if (!pendingSave || !templateId || !pageTemplate) return;
    updateTemplateMutation.mutate(
      {
        id: templateId,
        payload: {
          name: pageTemplate.name,
          description: pageTemplate.description,
          config: pendingSave.nextConfig,
        },
      },
      {
        onSuccess: (data) => {
          setComponents(pendingSave.nextComponents);
          setDataEntryComponents(pendingSave.nextDataEntryComponents);
          setCurrentLayout(pendingSave.nextLayout);
          setOriginalLayout(pendingSave.nextLayout);
          setIsEditingLayout(false);
          setIsSaveTemplateModalOpen(false);
          setPendingSave(null);

          const updated = data?.cascade?.updated_pages ?? 0;
          const summary =
            updated === 0
              ? 'Template saved.'
              : `Template saved. ${updated} dashboard${updated === 1 ? '' : 's'} updated.`;
          setBanner({ color: 'green', text: summary });
        },
        onError: () => {
          setIsSaveTemplateModalOpen(false);
          setPendingSave(null);
          setBanner({ color: 'red', text: 'Failed to save template.' });
        },
      },
    );
  };

  const handleCancelConfirm = () => {
    setIsSaveTemplateModalOpen(false);
    setPendingSave(null);
  };

  return {
    // State
    isEditingLayout,
    isEditDetailsOpen,
    isAddWidgetOpen,
    isSaveTemplateModalOpen,
    pendingSave,
    banner,
    templateHasWidgets,
    isUpdatingTemplate: updateTemplateMutation.isPending,
    // Setters consumed by JSX
    setIsEditingLayout,
    setIsEditDetailsOpen,
    setIsAddWidgetOpen,
    setBanner,
    // Handlers
    handleStartEditLayout,
    handleSaveLayout,
    handleCancelLayout,
    handleResetLayout,
    handleEditDetails,
    handleAddWidget,
    handleEditWidget,
    handleDeleteWidget,
    handleConfirmSave,
    handleCancelConfirm,
  };
};

export { useChildPageCount, useTemplateSaveActions };
